import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await ax.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Returns the signed-in user, OR {mfa_required|mfa_setup_required, mfa_token} for master admins,
  // who must complete a second step (authenticator code) before they get a session.
  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.mfa_required || data.mfa_setup_required) return data;
      setUser(data);
      return data;
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const twoFactorSetup = async (mfaToken) => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, { mfa_token: mfaToken }, { withCredentials: true });
      return data; // { secret, otpauth_uri }
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const twoFactorEnable = async (mfaToken, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/enable`, { mfa_token: mfaToken, code }, { withCredentials: true });
      setUser(data.user);
      return data; // { user, recovery_codes }
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const twoFactorVerify = async (mfaToken, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { mfa_token: mfaToken, code }, { withCredentials: true });
      setUser(data);
      return data;
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const register = async (formData) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, formData, { withCredentials: true });
      setUser(data);
      return data;
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const refreshUser = async () => {
    try {
      const { data } = await ax.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, refreshUser, setUser, twoFactorSetup, twoFactorEnable, twoFactorVerify }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
